import { NAVY, GOLD, WHITE, fmt, isOver, PRIORITY_COLORS, STATUS_COLORS } from '../lib/ui';
import { Pill, Card, Empty, Avatar } from '../lib/ui';

export default function Dashboard({ matters, team, allTasks, setView, setActiveMatter, setFilterPreset }) {
  const open     = matters.filter(m => m.status !== 'Closed' && m.status !== 'Completed').length;
  const critical = matters.filter(m => m.priority === 'Critical').length;
  const inProg   = allTasks.filter(t => t.status === 'In Progress').length;
  const overdue  = allTasks.filter(t => isOver(t.deadline) && t.status !== 'Completed' && t.status !== 'Closed');
  const urgentM  = matters.filter(m => isOver(m.deadline) || m.priority === 'Critical');

  const memberName = id => team.find(t => t.id === id)?.name || '—';

  const KPI_CARDS = [
    {
      label: 'Open Matters', value: open, color: NAVY,
      hint: 'View all open matters →',
      onClick: () => setView('matters'),
    },
    {
      label: 'Critical Priority', value: critical, color: '#C0392B',
      hint: 'View critical matters →',
      onClick: () => { setFilterPreset && setFilterPreset({ priority: 'Critical' }); setView('matters'); },
    },
    {
      label: 'Tasks In Progress', value: inProg, color: '#2980B9',
      hint: 'View all tasks →',
      onClick: () => { setFilterPreset && setFilterPreset({ status: 'In Progress' }); setView('tasks'); },
    },
    {
      label: 'Overdue Tasks', value: overdue.length, color: '#E67E22',
      hint: 'View overdue tasks →',
      onClick: () => { setFilterPreset && setFilterPreset({ overdue: true }); setView('tasks'); },
    },
  ];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
      {/* KPI strip — clickable */}
      <div className="kpi-strip" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16 }}>
        {KPI_CARDS.map(({ label, value, color, hint, onClick }) => (
          <div key={label} onClick={onClick} style={{
            background:WHITE, borderRadius:10, padding:'18px 20px',
            border:'1px solid #E5E0D8', borderTop:`3px solid ${color}`,
            cursor:'pointer', transition:'box-shadow 0.15s, transform 0.1s',
          }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.1)'; e.currentTarget.style.transform='translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow='none'; e.currentTarget.style.transform='none'; }}>
            <div style={{ fontSize:30, fontWeight:700, color, fontFamily:'Georgia,serif' }}>{value}</div>
            <div style={{ fontSize:12, color:'#777', marginTop:4, fontWeight:600 }}>{label}</div>
            <div style={{ fontSize:10, color:color, marginTop:6, opacity:0.7 }}>{hint}</div>
          </div>
        ))}
      </div>

      <div className="grid-2col" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        {/* Urgent matters */}
        <Card title="Urgent & Overdue Matters">
          {urgentM.length === 0
            ? <Empty text="No urgent matters — all clear!" />
            : urgentM.slice(0,6).map(m => (
              <div key={m.id} onClick={() => { setView('matters'); setActiveMatter(m.id); }}
                style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid #F0EDE8', cursor:'pointer' }}>
                <Pill label={m.priority} color={PRIORITY_COLORS[m.priority]} />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:NAVY }}>{m.name}</div>
                  <div style={{ fontSize:11, color:'#888' }}>{m.client} · Due {fmt(m.deadline)}</div>
                </div>
                {isOver(m.deadline) && <Pill label="Overdue" color="#C0392B" size={10} />}
              </div>
            ))
          }
        </Card>

        {/* Overdue tasks */}
        <Card title="Overdue Tasks">
          {overdue.length === 0
            ? <Empty text="No overdue tasks!" />
            : overdue.slice(0,6).map(t => (
              <div key={t.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid #F0EDE8' }}>
                <Pill label={t.priority} color={PRIORITY_COLORS[t.priority]} />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:NAVY }}>{t.title}</div>
                  <div style={{ fontSize:11, color:'#888' }}>{t.matterName} · {(t.assignees||[]).map(memberName).join(', ')}</div>
                </div>
                <span style={{ fontSize:11, color:'#C0392B', fontWeight:700 }}>Due {fmt(t.deadline)}</span>
              </div>
            ))
          }
        </Card>

        {/* Team workload */}
        <Card title="Team Workload — click a member to see their tasks">
          {team.map(m => {
            const active   = allTasks.filter(t => (t.assignees||[]).includes(m.id) && t.status === 'In Progress').length;
            const total    = allTasks.filter(t => (t.assignees||[]).includes(m.id)).length;
            const overdue  = allTasks.filter(t => (t.assignees||[]).includes(m.id) && t.status !== 'Completed' && t.status !== 'Closed' && t.deadline && new Date(t.deadline) < new Date()).length;
            return (
              <div key={m.id}
                onClick={() => {
                  sessionStorage.setItem('kl_view_member', m.id);
                  setView('mytasks');
                }}
                style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid #F0EDE8', cursor:'pointer', borderRadius:6, transition:'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#FAFAF8'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <Avatar name={m.name} size={32} />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:NAVY }}>{m.name}</div>
                  <div style={{ fontSize:11, color:'#888' }}>{m.role}</div>
                </div>
                <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                  {overdue > 0 && <span style={{ fontSize:11, color:'#C0392B', fontWeight:700 }}>⚠ {overdue} overdue</span>}
                  <span style={{ fontSize:12, color: active>3?'#C0392B':NAVY, fontWeight:700 }}>{active} active / {total} total</span>
                  <span style={{ fontSize:11, color:'#BBB' }}>→</span>
                </div>
              </div>
            );
          })}
        </Card>

        {/* Recent matters */}
        <Card title="Recent Matters">
          {matters.slice(0,6).map(m => (
            <div key={m.id} onClick={() => { setView('matters'); setActiveMatter(m.id); }}
              style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid #F0EDE8', cursor:'pointer' }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:600, color:NAVY }}>{m.name}</div>
                <div style={{ fontSize:11, color:'#888' }}>{m.client} · {(m.tasks||[]).length} tasks</div>
              </div>
              <Pill label={m.status} color={STATUS_COLORS[m.status]} size={11} />
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
